import type { EnvironmentId } from "@cmmd-forge/contracts";
import {
  CircleCheckIcon,
  CircleDotIcon,
  CircleXIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  TriangleAlertIcon,
} from "lucide-react";

import { Skeleton } from "../../components/ui/skeleton";
import { cn } from "~/lib/utils";
import { formatRelativeTimeLabel } from "~/timestampFormat";
import type { PullRequestInboxRow } from "./pullRequests.inbox.logic";

export function PullRequestInboxLoading() {
  return (
    <div aria-label="Loading pull requests">
      <PullRequestSkeletonGroup label="Reviewing" rows={2} />
      <PullRequestSkeletonGroup label="Others" rows={3} />
    </div>
  );
}

function PullRequestSkeletonGroup(props: { readonly label: string; readonly rows: number }) {
  return (
    <section className="mb-3 last:mb-0">
      <h2 className="px-3 pb-0.5 text-xs font-medium text-muted-foreground/70">{props.label}</h2>
      <div className="space-y-1" aria-hidden="true">
        {Array.from({ length: props.rows }, (_, index) => (
          <div
            key={index}
            data-testid="pull-request-skeleton-row"
            className="flex min-h-touch items-center gap-3 rounded-[var(--control-radius)] px-3 py-2 md:min-h-0"
          >
            <Skeleton className="size-4 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-3/5" />
              <Skeleton className="h-3 w-2/5" />
            </div>
            <Skeleton className="h-5 w-12 rounded-full" />
          </div>
        ))}
      </div>
    </section>
  );
}

export function PullRequestRows(props: {
  readonly rows: ReadonlyArray<PullRequestInboxRow>;
  readonly activeRowKey: string | undefined;
  readonly environmentLabels: ReadonlyMap<EnvironmentId, string>;
  readonly onOpen: (row: PullRequestInboxRow) => void;
}) {
  return (
    <ul className="space-y-0.5">
      {props.rows.map((row) => (
        <PullRequestRow
          key={row.rowKey}
          row={row}
          selected={row.rowKey === props.activeRowKey}
          environmentLabel={
            props.environmentLabels.size > 1
              ? props.environmentLabels.get(row.environmentId)
              : undefined
          }
          onOpen={props.onOpen}
        />
      ))}
    </ul>
  );
}

function PullRequestRow(props: {
  readonly row: PullRequestInboxRow;
  readonly selected: boolean;
  readonly environmentLabel: string | undefined;
  readonly onOpen: (row: PullRequestInboxRow) => void;
}) {
  const { row } = props;
  const showVerdict =
    row.reviewDecision === "approved" || row.reviewDecision === "changes-requested";
  return (
    <li>
      <button
        type="button"
        aria-current={props.selected ? "true" : undefined}
        className={cn(
          "grid min-h-touch w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-[var(--control-radius)] px-3 py-2 text-left transition-colors [contain-intrinsic-block-size:54px] [content-visibility:auto] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:h-[54px] md:min-h-0",
          props.selected ? "bg-accent" : "hover:bg-accent/60",
        )}
        onClick={() => props.onOpen(row)}
      >
        <PullRequestStateGlyph row={row} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">{row.title}</span>
          <span className="mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-xs text-muted-foreground/70">
            <PullRequestMetaSegment>#{row.number}</PullRequestMetaSegment>
            <PullRequestMetaSegment className="truncate">{row.repository}</PullRequestMetaSegment>
            {props.environmentLabel === undefined ? null : (
              <PullRequestMetaSegment className="hidden max-w-32 truncate sm:flex">
                {props.environmentLabel}
              </PullRequestMetaSegment>
            )}
            <PullRequestActor actor={row.author} />
            {showVerdict ? <PullRequestVerdict decision={row.reviewDecision} /> : null}
            <PullRequestChecksState state={row.checksState} />
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-0.5 text-xs text-muted-foreground/70 tabular-nums">
          <span>{formatRelativeTimeLabel(row.updatedAt)}</span>
          <PullRequestDiffStat additions={row.additions ?? 0} deletions={row.deletions ?? 0} />
        </span>
      </button>
    </li>
  );
}

function PullRequestDiffStat(props: { readonly additions: number; readonly deletions: number }) {
  if (props.additions === 0 && props.deletions === 0) return null;
  return (
    <span className="hidden items-baseline gap-1 sm:inline-flex">
      <span className="text-success-foreground">+{props.additions.toLocaleString()}</span>
      <span className="text-destructive">-{props.deletions.toLocaleString()}</span>
    </span>
  );
}

function PullRequestMetaSegment(props: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1.5 before:text-muted-foreground/45 before:content-['·'] first:before:hidden",
        props.className,
      )}
    >
      {props.children}
    </span>
  );
}

function PullRequestActor({ actor }: { readonly actor: PullRequestInboxRow["author"] }) {
  const login = actor?.login ?? "ghost";
  return (
    <PullRequestMetaSegment className="hidden max-w-40 sm:flex">
      {actor?.avatarUrl == null ? (
        <span
          aria-hidden="true"
          className="flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-medium text-muted-foreground"
        >
          {login.slice(0, 1).toUpperCase()}
        </span>
      ) : (
        <img
          aria-hidden="true"
          alt=""
          src={actor.avatarUrl}
          loading="lazy"
          className="size-4 shrink-0 rounded-full bg-muted object-cover"
        />
      )}
      <span className="truncate">{login}</span>
    </PullRequestMetaSegment>
  );
}

function PullRequestChecksState(props: { readonly state: PullRequestInboxRow["checksState"] }) {
  if (props.state === undefined) return null;
  const presentation = {
    passing: {
      label: "All checks have passed",
      Icon: CircleCheckIcon,
      tone: "text-success-foreground",
    },
    failing: {
      label: "Some checks were not successful",
      Icon: CircleXIcon,
      tone: "text-destructive",
    },
    pending: {
      label: "Some checks have not completed yet",
      Icon: CircleDotIcon,
      tone: "text-warning-foreground",
    },
  }[props.state];
  return (
    <PullRequestMetaSegment className="hidden sm:flex">
      <presentation.Icon
        role="img"
        aria-label={presentation.label}
        className={cn("size-3.5 shrink-0", presentation.tone)}
      />
    </PullRequestMetaSegment>
  );
}

function PullRequestVerdict(props: { readonly decision: PullRequestInboxRow["reviewDecision"] }) {
  return (
    <PullRequestMetaSegment
      className={cn(
        props.decision === "approved" ? "text-success-foreground" : "text-warning-foreground",
      )}
    >
      {props.decision === "approved" ? "Approved" : "Changes requested"}
    </PullRequestMetaSegment>
  );
}

function PullRequestStateGlyph({ row }: { readonly row: PullRequestInboxRow }) {
  const presentation =
    row.state === "merged"
      ? { label: "Merged", Icon: GitMergeIcon, tone: "text-hue-violet-foreground" }
      : row.state === "closed"
        ? { label: "Closed", Icon: GitPullRequestClosedIcon, tone: "text-destructive" }
        : row.isDraft
          ? { label: "Draft", Icon: GitPullRequestDraftIcon, tone: "text-muted-foreground" }
          : row.mergeability === "conflicting"
            ? {
                label:
                  row.baseBranch === undefined
                    ? "Has conflicts"
                    : `Conflicts with ${row.baseBranch}`,
                Icon: TriangleAlertIcon,
                tone: "text-destructive",
              }
            : { label: "Open", Icon: GitPullRequestIcon, tone: "text-success-foreground" };
  return (
    <presentation.Icon
      role="img"
      aria-label={presentation.label}
      className={cn("size-4 shrink-0", presentation.tone)}
    />
  );
}
